// Copyright (c) 2026 Yunus YILDIZ — SPDX-License-Identifier: BUSL-1.1

import { createContext, useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import type { User, LoginCredentials, LoginResult, RegisterPayload, AuthTokens } from '@/types'
import { getErrorMessage } from '@/lib/utils/errorHandler'
import { tokenStorage } from './tokenStorage'
import { API_BASE_URL } from '@/config/api'

export interface AuthContextValue {
  user: User | null
  isLoading: boolean
  isAuthenticated: boolean
  login: (credentials: LoginCredentials) => Promise<LoginResult>
  register: (payload: RegisterPayload) => Promise<void>
  /** Apply already-obtained tokens + user data (e.g. after invite accept) */
  applyAuthResult: (tokens: AuthTokens, userData: User) => void
  /** Poll Microsoft OAuth flow after redirect (portal web only) */
  completeMicrosoftSignIn: (flowId: string, pollToken: string) => Promise<void>
  logout: () => Promise<void>
  refreshToken: () => Promise<void>
}

export const AuthContext = createContext<AuthContextValue | null>(null)

const REFRESH_INTERVAL_MS = 4 * 60 * 1000 // 4 minutes

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    credentials: init?.credentials ?? 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...init?.headers,
    },
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({ message: res.statusText }))
    throw { ...body, statusCode: res.status }
  }
  return res.json() as Promise<T>
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const applyTokens = useCallback((tokens: AuthTokens, userData: User) => {
    tokenStorage.setAccessToken(tokens.accessToken)
    tokenStorage.setRefreshToken(tokens.refreshToken)
    setUser(userData)
  }, [])

  const scheduleRefresh = useCallback(() => {
    if (refreshTimerRef.current) clearInterval(refreshTimerRef.current)
    refreshTimerRef.current = setInterval(async () => {
      try {
        await doRefresh()
      } catch {
        tokenStorage.clearAll()
        setUser(null)
      }
    }, REFRESH_INTERVAL_MS)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const doRefresh = useCallback(async () => {
    const rt = tokenStorage.getRefreshToken()
    if (!rt) throw new Error('No refresh token')

    const data = await apiFetch<{ tokens: AuthTokens; user: User }>('/v1/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refreshToken: rt }),
    })
    applyTokens(data.tokens, data.user)
    scheduleRefresh()
  }, [applyTokens, scheduleRefresh])

  // Restore session on mount
  useEffect(() => {
    doRefresh()
      .catch(() => {
        tokenStorage.clearAll()
      })
      .finally(() => setIsLoading(false))

    // Broadcast logout across tabs
    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'sdf_logout') {
        tokenStorage.clearAll()
        setUser(null)
      }
    }
    window.addEventListener('storage', handleStorage)
    return () => {
      window.removeEventListener('storage', handleStorage)
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current)
    }
  }, [doRefresh])

  const login = useCallback(
    async (credentials: LoginCredentials): Promise<LoginResult> => {
      const body =
        credentials.mfaToken != null
          ? { mfaToken: credentials.mfaToken, mfaCode: credentials.mfaCode ?? '' }
          : { email: credentials.email ?? '', password: credentials.password ?? '' }

      const res = await fetch(`${API_BASE_URL}/v1/auth/login`, {
        method: 'POST',
        credentials: 'include',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const data = await res.json().catch(() => ({ message: res.statusText }))
      if (!res.ok) {
        throw { ...data, statusCode: res.status }
      }
      if (data && typeof data === 'object' && 'mfaRequired' in data && data.mfaRequired && 'mfaToken' in data) {
        return { mfaRequired: true, mfaToken: String(data.mfaToken) }
      }
      const auth = data as { tokens: AuthTokens; user: User }
      applyTokens(auth.tokens, auth.user)
      scheduleRefresh()
      return { ok: true }
    },
    [applyTokens, scheduleRefresh]
  )

  const register = useCallback(
    async (payload: RegisterPayload) => {
      const data = await apiFetch<{ tokens: AuthTokens; user: User }>('/v1/auth/register', {
        method: 'POST',
        body: JSON.stringify(payload),
      })
      applyTokens(data.tokens, data.user)
      scheduleRefresh()
    },
    [applyTokens, scheduleRefresh]
  )

  const completeMicrosoftSignIn = useCallback(
    async (flowId: string, pollToken: string) => {
      const deadline = Date.now() + 12 * 60 * 1000
      while (Date.now() < deadline) {
        const res = await fetch(
          `${API_BASE_URL}/v1/auth/microsoft/poll?flowId=${encodeURIComponent(flowId)}&pollToken=${encodeURIComponent(pollToken)}`,
          { headers: { Accept: 'application/json' } }
        )
        if (!res.ok) {
          const body = await res.json().catch(() => ({ message: res.statusText }))
          throw new Error(getErrorMessage({ ...body, statusCode: res.status }))
        }
        const data = (await res.json()) as
          | { status: 'pending'; expiresIn: number }
          | { status: 'failed'; error: string }
          | { status: 'completed'; tokens: AuthTokens; user: User }

        if (data.status === 'failed') {
          throw new Error(data.error)
        }
        if (data.status === 'completed') {
          applyTokens(data.tokens, data.user)
          scheduleRefresh()
          return
        }
        await new Promise((r) => setTimeout(r, 1500))
      }
      throw new Error('Microsoft sign-in timed out. Please try again.')
    },
    [applyTokens, scheduleRefresh]
  )

  const logout = useCallback(async () => {
    const at = tokenStorage.getAccessToken()
    const rt = tokenStorage.getRefreshToken()
    if (at || rt) {
      await apiFetch('/v1/auth/logout', {
        method: 'POST',
        headers: at ? { Authorization: `Bearer ${at}` } : {},
        body: rt ? JSON.stringify({ refreshToken: rt }) : undefined,
      }).catch(() => null)
    }
    tokenStorage.clearAll()
    setUser(null)
    if (refreshTimerRef.current) clearInterval(refreshTimerRef.current)
    // Signal other tabs
    localStorage.setItem('sdf_logout', Date.now().toString())
    localStorage.removeItem('sdf_logout')
  }, [])

  const applyAuthResult = useCallback(
    (tokens: AuthTokens, userData: User) => {
      applyTokens(tokens, userData)
      scheduleRefresh()
    },
    [applyTokens, scheduleRefresh],
  )

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        register,
        applyAuthResult,
        completeMicrosoftSignIn,
        logout,
        refreshToken: doRefresh,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
